// pages/Rewards.jsx — My Points, Leaderboard, Redemptions, Badges
import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Trophy, Zap, Target, Lightbulb, Users2, Flame, BookOpen, Award, Gift, TrendingUp } from 'lucide-react';

const BADGE_ICONS = { productivity_champion: Trophy, speed_master: Zap, quality_expert: Target, innovation_award: Lightbulb, team_player: Users2, consistency_star: Flame, documentation_expert: BookOpen };

export default function Rewards() {
  const { user } = useAuth();
  const [tab, setTab] = useState('me');
  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="rewards-page">
      <div style={{ color: 'var(--trello-text)', marginBottom: 24 }}>
        <div className="flex items-center gap-3"><Award size={26} /><h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Rewards & Performance</h1></div>
        <p style={{ opacity: 0.85, marginTop: 4 }}>Earn points by completing tasks on time, collaborating, and delivering quality work.</p>
      </div>
      <div className="flex gap-2 mb-4" data-testid="rewards-tabs">
        {[
          { k: 'me', l: 'My Points' },
          { k: 'leaderboard', l: 'Leaderboard' },
          { k: 'redemptions', l: 'Redemptions' },
          { k: 'rules', l: 'Point Rules' },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`rw-tab-${t.k}`}
            className={tab === t.k ? "tm-btn" : "tm-btn-ghost"}
            style={{ borderRadius: 20, padding: '8px 16px', fontSize: 13, fontWeight: 600 }}>
            {t.l}
          </button>
        ))}
      </div>
      {tab === 'me' && <MyPoints user={user} />}
      {tab === 'leaderboard' && <Leaderboard />}
      {tab === 'redemptions' && <Redemptions />}
      {tab === 'rules' && <PointRules />}
    </div>
  );
}

function MyPoints({ user }) {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get('/rewards/me').then(({ data }) => setData(data));
    api.get('/rewards/events?limit=30').then(({ data }) => setEvents(data.events));
  }, []);
  if (!data) return <div className="tm-stat-card" style={{ borderLeft: 'none' }}>Loading…</div>;
  const nextLevel = ['Beginner', 'Performer', 'Achiever', 'Expert', 'Champion', 'Legend']
    .find((n, i, arr) => arr[i - 1] === data.level.name) || data.level.name;
  const pct = data.level.max === Infinity ? 100 : Math.round(((data.total_points - data.level.min) / (data.level.max - data.level.min)) * 100);
  return (
    <>
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div className="tm-stat-tinted" style={{ '--tint': '#F2D600', '--tint-bg': '#FFF7E6' }} data-testid="rw-total">
          <div className="flex items-center gap-2 mb-1"><Trophy size={16} color="#B08800" /><span className="tm-stat-label">Total points</span></div>
          <div className="tm-stat-value" style={{ color: '#B08800' }}>{data.total_points}</div>
        </div>
        <div className="tm-stat-tinted" style={{ '--tint': '#0079BF', '--tint-bg': '#E4F0F6' }} data-testid="rw-month">
          <div className="flex items-center gap-2 mb-1"><TrendingUp size={16} color="#0079BF" /><span className="tm-stat-label">This month</span></div>
          <div className="tm-stat-value" style={{ color: '#0079BF' }}>{data.month_points}</div>
        </div>
        <div className="tm-stat-tinted" style={{ '--tint': '#89609E', '--tint-bg': '#F3ECF7' }} data-testid="rw-level">
          <div className="flex items-center gap-2 mb-1"><Award size={16} color="#89609E" /><span className="tm-stat-label">Level</span></div>
          <div className="tm-stat-value" style={{ color: '#89609E', fontSize: 24 }}>{data.level.name}</div>
          {data.level.max !== Infinity && (
            <div style={{ marginTop: 6 }}>
              <div className="tm-progress-track"><div className="tm-progress-fill" style={{ width: `${pct}%`, background: '#89609E' }} /></div>
              <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 2 }}>{data.level.max - data.total_points + 1} pts to next level</div>
            </div>
          )}
        </div>
      </div>

      {data.badges.length > 0 && (
        <div className="tm-stat-card mb-4" style={{ borderLeft: 'none' }} data-testid="rw-badges">
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>Current month badges</h3>
          <div className="flex gap-2 flex-wrap">
            {data.badges.map((b) => {
              const Icon = BADGE_ICONS[b.key] || Award;
              return (
                <div key={b.key} data-testid={`badge-${b.key}`}
                  style={{ background: 'linear-gradient(135deg,#F2D600,#FF8B00)', color: 'var(--trello-text)', padding: '8px 12px', borderRadius: 20, display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                  <span style={{ fontSize: 16 }}>{b.emoji}</span> {b.label}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>This month by type</h3>
          {data.breakdown.length === 0 && <div style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>No points yet — start with a task.</div>}
          <table style={{ width: '100%', fontSize: 13 }}>
            <tbody>
              {data.breakdown.map((b) => (
                <tr key={b.event_type} data-testid={`bd-${b.event_type}`}>
                  <td style={{ padding: '4px 0' }}>{b.event_type.replace(/_/g, ' ')}</td>
                  <td style={{ textAlign: 'right', color: 'var(--trello-text-muted)' }}>{b.n}×</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: Number(b.pts) >= 0 ? '#519839' : '#DE350B' }}>{Number(b.pts) >= 0 ? '+' : ''}{b.pts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>Recent activity</h3>
          {events.length === 0 && <div style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>No activity yet.</div>}
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {events.map((e) => (
              <div key={e.id} className="flex items-center justify-between" style={{ padding: '6px 0', borderTop: '1px solid var(--trello-border)', fontSize: 13 }} data-testid={`ev-${e.id}`}>
                <div>
                  <div>{e.event_type.replace(/_/g, ' ')}</div>
                  {e.task_title && <div style={{ fontSize: 11, color: 'var(--trello-text-muted)' }}>{e.task_title}</div>}
                </div>
                <div style={{ fontWeight: 700, color: e.points >= 0 ? '#519839' : '#DE350B' }}>{e.points >= 0 ? '+' : ''}{e.points}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function Leaderboard() {
  const [board, setBoard] = useState([]);
  const [month, setMonth] = useState('');
  useEffect(() => { api.get('/rewards/leaderboard').then(({ data }) => { setBoard(data.leaderboard); setMonth(data.month); }); }, []);
  return (
    <div className="tm-stat-card" style={{ borderLeft: 'none' }} data-testid="leaderboard">
      <div className="flex items-center justify-between mb-3">
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Leaderboard · {month}</h3>
      </div>
      <table style={{ width: '100%', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--trello-text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
            <th style={{ padding: '6px 4px', width: 40 }}>#</th>
            <th>Employee</th>
            <th>Tasks Completed</th>
            <th>On-Time %</th>
            <th>Quality</th>
            <th>Points</th>
            <th>Badges</th>
          </tr>
        </thead>
        <tbody>
          {board.map((r, idx) => (
            <tr key={r.id} data-testid={`lb-row-${r.id}`} style={{ borderTop: '1px solid var(--trello-border)' }}>
              <td style={{ padding: '10px 4px', fontWeight: 700 }}>{idx + 1}{idx === 0 && ' 🏆'}</td>
              <td>
                <div className="flex items-center gap-2">
                  <div className="tm-avatar" style={{ width: 26, height: 26, fontSize: 11, background: r.avatar_color || 'var(--trello-text-muted)' }}>{(r.full_name || '?').split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase()}</div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.full_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--trello-text-muted)' }}>{r.level?.name}</div>
                  </div>
                </div>
              </td>
              <td>{r.tasks_completed}</td>
              <td>{r.on_time_rate}%</td>
              <td>{r.quality_score || '—'}</td>
              <td style={{ fontWeight: 700, color: '#0079BF' }}>{r.month_points}</td>
              <td>
                <div className="flex flex-wrap gap-1">
                  {(r.badges || []).map((b) => (
                    <span key={b.key} title={b.label} data-testid={`lb-badge-${r.id}-${b.key}`} style={{ fontSize: 16 }}>{b.emoji}</span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Redemptions() {
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState([]);
  const load = () => {
    api.get('/rewards/me').then(({ data }) => setMe(data));
    api.get('/rewards/redemptions').then(({ data }) => setHistory(data.redemptions));
  };
  useEffect(load, []);
  const redeem = async (key) => {
    if (!confirm('Redeem this reward?')) return;
    try { await api.post('/rewards/redemptions', { reward_key: key }); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };
  if (!me) return <div className="tm-stat-card" style={{ borderLeft: 'none' }}>Loading…</div>;
  return (
    <div>
      <div className="tm-stat-card mb-4" style={{ borderLeft: 'none' }}>
        <div className="flex items-center justify-between">
          <div>
            <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', textTransform: 'uppercase' }}>Your balance</div>
            <div style={{ fontSize: 30, fontWeight: 700, color: '#B08800' }}>{me.total_points} pts</div>
          </div>
          <Gift size={40} color="#F2D600" />
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3 mb-4" data-testid="catalog">
        {me.catalog.map((r) => {
          const affordable = me.total_points >= r.points_cost;
          return (
            <div key={r.key} className="tm-stat-card" style={{ borderLeft: 'none', opacity: affordable ? 1 : 0.55 }} data-testid={`redeem-${r.key}`}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{r.label}</div>
              <div style={{ fontSize: 13, color: 'var(--trello-text-muted)', margin: '4px 0 10px' }}>{r.points_cost.toLocaleString()} pts</div>
              <button className="tm-btn w-full" onClick={() => redeem(r.key)} disabled={!affordable}
                data-testid={`redeem-btn-${r.key}`}>
                {affordable ? 'Redeem' : `Need ${r.points_cost - me.total_points} more`}
              </button>
            </div>
          );
        })}
      </div>
      <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>Your redemption history</h3>
        {history.length === 0 && <div style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>You haven&apos;t redeemed anything yet.</div>}
        {history.map((h) => (
          <div key={h.id} className="flex items-center justify-between" style={{ padding: '6px 0', borderTop: '1px solid var(--trello-border)', fontSize: 13 }} data-testid={`redemp-${h.id}`}>
            <div>
              <div style={{ fontWeight: 600 }}>{h.reward_label}</div>
              <div style={{ fontSize: 11, color: 'var(--trello-text-muted)' }}>{new Date(h.created_at).toLocaleDateString()}</div>
            </div>
            <div className="flex items-center gap-2">
              <span style={{ fontWeight: 700 }}>-{h.points_cost} pts</span>
              <span className="tm-pill" style={{ background: h.status === 'fulfilled' ? '#E6F4EA' : h.status === 'denied' ? '#FBEDEA' : '#FFF7E6', color: h.status === 'fulfilled' ? '#0B6623' : h.status === 'denied' ? '#B04632' : '#7F5A00' }}>{h.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PointRules() {
  const AWARDS = [
    ['Create a new task', 2], ['Complete task before due date', 10], ['Complete task on due date', 8],
    ['Complete within 2 days late', 3], ['More than 2 days late', 0],
    ['High-priority task early', 15], ['Critical task early (extra)', 20],
    ['Task approved (no revisions)', 5], ['Deletion suggestion submitted', 3], ['Deletion approved', 10],
    ['Help teammate', 5], ['Documentation/attachment', 2], ['Meaningful comment (max 5/task)', 1],
  ];
  const PENALTIES = [
    ['Revision requested', -3], ['Major rework', -8], ['Miss due date', -5],
    ['Task reopened', -5], ['Incorrect update', -2], ['Incomplete documentation', -2], ['Abandon task', -10],
  ];
  const QUALITY = [['Excellent', 10], ['Good', 5], ['Average', 2], ['Poor', 0]];
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="rules-page">
      <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>Rewards</h3>
        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>{AWARDS.map(([l, p]) => <tr key={l}><td style={{ padding: '4px 0' }}>{l}</td><td style={{ textAlign: 'right', color: '#519839', fontWeight: 700 }}>+{p}</td></tr>)}</tbody>
        </table>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '20px 0 10px' }}>Quality bonus</h3>
        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>{QUALITY.map(([l, p]) => <tr key={l}><td style={{ padding: '4px 0' }}>{l}</td><td style={{ textAlign: 'right', color: '#519839', fontWeight: 700 }}>+{p}</td></tr>)}</tbody>
        </table>
      </div>
      <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>Penalties</h3>
        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>{PENALTIES.map(([l, p]) => <tr key={l}><td style={{ padding: '4px 0' }}>{l}</td><td style={{ textAlign: 'right', color: '#DE350B', fontWeight: 700 }}>{p}</td></tr>)}</tbody>
        </table>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '20px 0 10px' }}>Anti-abuse & Distribution rules</h3>
        <ul style={{ fontSize: 13, paddingLeft: 20, lineHeight: 1.7 }}>
          <li>Task completion rewards, priority bonuses, and late penalties are divided equally among all assignees (rounded, min +1 for awards, max -1 for penalties)</li>
          <li>Max reward for 10 task creations per user per day</li>
          <li>Comment reward capped at 5 per task per user</li>
          <li>Duplicate task creation dedup&apos;d automatically</li>
          <li>Employees cannot rate their own work</li>
          <li>Managers cannot approve their own deletion requests</li>
          <li>Only Leaders can approve deletion suggestions</li>
          <li>All reward activity is audited</li>
        </ul>
      </div>
    </div>
  );
}
